import { sessionResumeRecording } from '@/lib/analytics/scribe/session';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';

import {
  CHRONICLE_ORIGIN,
  chronicleSessionIdAtom,
  type ChronicleRecordingOrigin,
} from '@/atoms/chronicle';
import { useChronicleStartRecording } from '@/hooks/desktop/useChronicleStartRecording';
import {
  cancelRecordingAttempt,
  finishRecordingAttempt,
  type RecordingAttemptTracking,
} from '@/lib/telemetry/recordingStartTracking';
import { store, type Dispatch } from '@/store';
import { chronicleBridge } from '@/tauri/chronicle';
import { chronicleSessionBridge } from '@/tauri/chronicle/session';
import {
  trackHardwareRecordingResume,
  trackHardwareRecordingResumeFailed,
} from '@/utils/analytics/track-hardware-events';
import { chronicleLogger as logger } from '@/utils/logger/chronicle-logger';
import { getActiveTrackings, TrackingKey } from '@heidi/runtime/performance';
import { RecordingStatus } from '@heidi/schema/ml-scribe';
import { GET_SESSION_BY_ID, type QuerySessionResponse } from '@heidi/session/contracts';

/**
 * Core hook for resuming a Chronicle recording session.
 *
 * Handles both desktop-initiated and device-initiated resumes:
 *
 * **Desktop-initiated** (user clicks resume on desktop):
 * - `useRecordingButton` has already updated query cache with
 *   `recording_status: RECORDING` before this callback fires.
 *   Note: `useMutateUpdateSessionRecordingStatus` does NOT PATCH the
 *   backend for RECORDING status (early return) — consistent with the
 *   original desktop flow.
 * - This hook sends the BLE command to the device, resumes the timer,
 *   and tracks analytics.
 *
 * **Device-initiated** (user presses resume on Chronicle device):
 * - `ChronicleStateProvider` updates `chronicleDeviceRecordingInfoAtom`
 *   with `isPaused: false`, which `useChronicleSessionControl` detects.
 * - This hook updates the query cache (since `useRecordingButton` is NOT
 *   involved), resumes the timer, and tracks analytics. Does NOT send BLE
 *   command (device already resumed).
 *
 * **Guards**:
 * - `chronicleSessionIdAtom` must be set (active session).
 * - `isResumingRef` prevents duplicate invocations from rapid clicks or
 *   simultaneous device + desktop actions (same pattern as `isStoppingRef`
 *   in the stop hook).
 *
 * Timer: calls `timerModel.onStart(currentSeconds)` which restarts the
 * timer interval from the paused position (`timerModel.seconds` was
 * preserved by `onPause()`).
 */
/**
 * Closes the click-site entry a DESKTOP resume opened. Gated on origin rather
 * than on the entry being absent: the contract counts desktop clicks, and a
 * device press must not inherit a success emit just because no entry happens to
 * be open at the time.
 */
function finishDesktopResumeAttempt(
  origin: ChronicleRecordingOrigin,
  tracking: RecordingAttemptTracking | undefined,
  error?: unknown
): void {
  if (origin !== CHRONICLE_ORIGIN.DESKTOP || !tracking) return;
  if (error !== undefined) finishRecordingAttempt(tracking, { outcome: 'failed', error });
  else finishRecordingAttempt(tracking);
}

/**
 * The re-arm was coalesced into a start that already owns the recorder, so this
 * click produced no attempt of its own. Not a failure — and emphatically not
 * the success a bare `await` used to report.
 */
function cancelDesktopResumeAttempt(
  origin: ChronicleRecordingOrigin,
  tracking: RecordingAttemptTracking | undefined
): void {
  if (origin !== CHRONICLE_ORIGIN.DESKTOP || !tracking) return;
  cancelRecordingAttempt(tracking);
}

export function useChronicleResumeRecording() {
  const dispatch = useDispatch<Dispatch>();
  const queryClient = useQueryClient();
  const chronicleSessionId = useAtomValue(chronicleSessionIdAtom);
  const { startChronicleRecording } = useChronicleStartRecording();
  const isResumingRef = useRef(false);

  const resumeChronicleRecording = useCallback(
    async (args: {
      origin: ChronicleRecordingOrigin;
      tracking?: RecordingAttemptTracking;
    }) => {
      if (args.origin === CHRONICLE_ORIGIN.DESKTOP && !args.tracking) {
        const entry = getActiveTrackings().get(TrackingKey.SCRIBE_RESUME_RECORDING);
        if (entry) {
          // Snapshot legacy direct callers synchronously; production button
          // paths pass the exact context explicitly.
          args.tracking = {
            trackingKey: TrackingKey.SCRIBE_RESUME_RECORDING,
            trackingInstanceId: entry.instanceId,
          };
        }
      }
      if (!chronicleSessionId) {
        logger.warn(
          '[useChronicleResumeRecording] No active Chronicle session, skipping resume'
        );
        // Nothing was resumed, so close the click-site entry rather than let it
        // replay as an abandoned attempt.
        if (args.tracking) cancelRecordingAttempt(args.tracking);
        return;
      }
      if (isResumingRef.current) {
        logger.warn(
          '[useChronicleResumeRecording] Already resuming, ignoring duplicate call'
        );
        // Deliberately leaves the click-site entry to the in-flight call rather
        // than closing it, matching the duplicate-start coalesce. A click that
        // loses this race therefore replays as `abandoned`.
        return;
      }
      isResumingRef.current = true;

      const sessionId = chronicleSessionId;

      try {
        // Re-arm via start when the Rust upload session is gone — a BLE-only
        // resume would record with no upload worker and drop the segment.
        let rustIsActive = false;
        try {
          const rustState = await chronicleSessionBridge.getSessionState();
          rustIsActive = rustState.isActive;
          logger.info('[remote] resume invoked', {
            origin: args.origin,
            chronicleSessionId: sessionId,
            rustIsActive: rustState.isActive,
            rustSessionId: rustState.sessionId,
          });
        } catch (stateErr) {
          // Unreadable state → treat as inactive and re-arm: safer than a
          // BLE-only resume that could silently drop the segment.
          logger.warn('[remote] getSessionState failed; re-arming via start', {
            origin: args.origin,
            chronicleSessionId: sessionId,
            error: stateErr instanceof Error ? stateErr.message : String(stateErr),
          });
        }

        if (!rustIsActive) {
          // HAR-1626: hand the live elapsed to the re-arm. A remote-first
          // recording has no Rust online session to find, so every resume takes
          // this branch — and `startChronicleRecording` would seed the timer
          // from the backend `duration`, still 0 until bulk sync. `onPause`
          // leaves `seconds` intact, and the recovery listener seeded it from
          // the device's own `MCU&RT` duration, so this is the true elapsed.
          const preservedSeconds = store.getState().timerModel.seconds;
          try {
            // `startChronicleRecording` RESOLVES on paths that started nothing
            // (an in-flight start, or another session already owning the
            // recorder). Reading the outcome is what stops a resume the device
            // never acted on from reporting a success.
            const outcome = await startChronicleRecording({
              origin: args.origin,
              targetSessionId: sessionId,
              resumeFromSeconds: preservedSeconds,
              tracking: args.tracking,
            });
            // Every non-`started` outcome means this click drove nothing:
            // coalesced into another start, no session to start, or another
            // session already owning the recorder. None is a failure of the
            // attempt, and none is a success.
            if (outcome === 'started') {
              finishDesktopResumeAttempt(args.origin, args.tracking);
            } else {
              cancelDesktopResumeAttempt(args.origin, args.tracking);
            }
          } catch (error) {
            // startChronicleRecording already toasts; swallow to keep resume non-throwing.
            logger.error('[useChronicleResumeRecording] re-arm via start failed:', error);
            finishDesktopResumeAttempt(args.origin, args.tracking, error);
          }
          return;
        }

        // 1. Desktop-initiated: send BLE command to resume device.
        //    Device-initiated: device already resumed — skip bridge call.
        if (args.origin === CHRONICLE_ORIGIN.DESKTOP) {
          try {
            await chronicleBridge.resumeRecording();
          } catch (error) {
            logger.error(
              '[useChronicleResumeRecording] chronicleBridge.resumeRecording failed:',
              error
            );
            trackHardwareRecordingResumeFailed({
              sessionIdLocal: sessionId,
              origin: args.origin,
              errorType: error instanceof Error ? error.name : 'UNKNOWN',
              errorDescription: error instanceof Error ? error.message : String(error),
            });
            // The device did not resume, so the attempt failed at the capture
            // layer even though the UI continues. `finishTracking` is
            // exactly-once, so this outcome wins over the success below.
            finishDesktopResumeAttempt(args.origin, args.tracking, error);
            // Continue — UI state should still reflect recording
          }
        }

        // 2. Device-initiated: update query cache to RECORDING.
        //    Desktop-initiated: already handled by useRecordingButton → skip.
        //    Note: no backend PATCH for RECORDING — consistent with
        //    useMutateUpdateSessionRecordingStatus which returns early.
        if (args.origin === CHRONICLE_ORIGIN.DEVICE) {
          queryClient.setQueryData<QuerySessionResponse>(
            [GET_SESSION_BY_ID, { sessionId }],
            (old) => {
              if (!old) return undefined;
              return { ...old, recording_status: RecordingStatus.RECORDING };
            }
          );
        }

        // 3. Resume timer from current seconds (preserved by onPause)
        const currentSeconds = store.getState().timerModel.seconds;
        dispatch.timerModel.onStart(currentSeconds);

        // 4. Track analytics
        sessionResumeRecording({
          sessionId,
        });
        trackHardwareRecordingResume({
          sessionId,
          sessionIdLocal: sessionId,
          origin: args.origin,
        });

        finishDesktopResumeAttempt(args.origin, args.tracking);

        logger.debug('[useChronicleResumeRecording] Recording resumed', {
          sessionId,
          origin: args.origin,
        });
      } finally {
        isResumingRef.current = false;
      }
    },
    [chronicleSessionId, dispatch, queryClient, startChronicleRecording]
  );

  return { resumeChronicleRecording };
}
